import React, { useEffect, useState } from 'react'
import { ShieldCheck, ShieldOff } from 'lucide-react'
import toast from 'react-hot-toast'
import { mfaApi, MfaFactor } from '../../services/api/mfa'
import { Button } from '../ui/Button'
import { LoadingSpinner } from '../ui/LoadingSpinner'
import { MfaEnrollDialog } from './MfaEnrollDialog'
import { MfaUnenrollDialog } from './MfaUnenrollDialog'

export const MfaSecuritySection: React.FC = () => {
  const [enrolled, setEnrolled] = useState(false)
  const [factors, setFactors] = useState<MfaFactor[]>([])
  const [loading, setLoading] = useState(true)
  const [enrollOpen, setEnrollOpen] = useState(false)
  const [unenrollOpen, setUnenrollOpen] = useState(false)

  const loadStatus = async () => {
    setLoading(true)
    try {
      const status = await mfaApi.getStatus()
      setEnrolled(status.enrolled)
      setFactors(status.factors)
    } catch {
      toast.error('Failed to load MFA status. Please refresh the page.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadStatus()
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="md" />
      </div>
    )
  }

  const primaryFactor = factors[0]

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-base font-semibold text-foreground">
          Authenticator app (TOTP)
        </h3>
        <p className="text-sm text-muted-foreground mt-1">
          Use a time-based one-time password app such as Google Authenticator, Authy, or 1Password
          to add a second layer of security to your account.
        </p>
      </div>

      {enrolled && primaryFactor ? (
        <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800/40">
          <div className="flex items-center gap-3">
            <ShieldCheck
              className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0"
              aria-hidden="true"
            />
            <div>
              <p className="text-sm font-medium text-green-800 dark:text-green-300">
                Authenticator app enrolled
              </p>
              <p className="text-xs text-green-700 dark:text-green-400 mt-0.5">
                Added {new Date(primaryFactor.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setUnenrollOpen(true)}
          >
            Remove
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between p-4 bg-muted rounded-lg border border-border">
          <div className="flex items-center gap-3">
            <ShieldOff
              className="h-5 w-5 text-muted-foreground flex-shrink-0"
              aria-hidden="true"
            />
            <p className="text-sm text-muted-foreground">Not enrolled</p>
          </div>
          <Button size="sm" onClick={() => setEnrollOpen(true)}>
            Set up authenticator app
          </Button>
        </div>
      )}

      <MfaEnrollDialog
        open={enrollOpen}
        onClose={() => setEnrollOpen(false)}
        onEnrolled={() => {
          setEnrollOpen(false)
          loadStatus()
        }}
      />

      {primaryFactor && (
        <MfaUnenrollDialog
          open={unenrollOpen}
          factorId={primaryFactor.id}
          onClose={() => setUnenrollOpen(false)}
          onUnenrolled={() => {
            setUnenrollOpen(false)
            loadStatus()
          }}
        />
      )}
    </div>
  )
}
